import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  GetAdvancedParams,
  ColumnValuesPayload,
  ColumnValuesResponse,
  GetParams,
  GetResponse,
  PostResponse,
} from '@app/shared';
import { CompanyService } from './company.service';
import { convertGetParamsToHttpParams } from '../utils/api-utils';
import { CostCenter } from '../../shared/models/cost-center';
import { ActivityType } from '../../shared/models/activity-type';

// Tipos de entidad padre para actividades (valores numéricos del enum del backend)
// Lead y Opportunity ambos usan 49, Project usa 5
export enum ParentEntityType {
  None = 0,
  LeadOrOpportunity = 49, // Lead y Opportunity ambos usan 49
  Project = 5, // Project usa 5
}

// Constantes para facilitar el uso
export const PARENT_ENTITY_TYPE = {
  NONE: 0,
  LEAD: 49, // Lead usa 49
  OPPORTUNITY: 49, // Opportunity también usa 49
  PROJECT: 5, // Project usa 5
  SALE_ORDER: 51, // SaleOrder (sale.order) - valor común en Odoo, verificar si es correcto
} as const;

// Estados de actividad
export type ActivityState = 'Planned' | 'InProgress' | 'Done' | 'Cancelled';

export interface ActivityUserPartner {
  id: number;
  avatar?: string;
  name?: string;
  surName?: string;
  position?: string;
  odooEntityId?: number;
}

export interface ActivityUser {
  id: number;
  email?: string;
  roleId?: number;
  partnerId?: number;
  partner?: ActivityUserPartner;
}

export interface Activity {
  id?: number;
  activityTypeId: number;
  activityType?: ActivityType;
  parentEntityType?: number; // 49 para Lead/Opportunity, 5 para Project
  parentEntityId?: number;
  userId?: number;
  user?: ActivityUser;
  companyId?: number;
  dateDeadLine?: string;
  dateCreated?: string;
  createdOn?: string;
  dateDone?: string;
  summary?: string;
  note?: string;
  state: ActivityState;
  hasExpired?: boolean;
}

export interface CreateActivityPayload {
  activityTypeId: number;
  parentEntityType: number | string; // 49 para Lead/Opportunity, 5 para Project, 'SaleOrder' para SaleOrder
  parentEntityId: number;
  userId?: number;
  companyId?: number;
  dateDeadLine?: string;
  summary?: string;
  note?: string;
  state?: ActivityState;
}

export interface UpdateActivityPayload {
  id: number;
  activityTypeId: number;
  parentEntityType: number | string; // 49 para Lead/Opportunity, 5 para Project, 'SaleOrder' para SaleOrder
  parentEntityId: number;
  userId?: number;
  companyId?: number;
  date?: string;
  summary?: string;
  note?: string;
  dateDeadLine?: string;
  state?: ActivityState;
  dateDone?: string;
}

export interface MarkAsDonePayload {
  id: number;
  parentEntityType: number | string; // 49 para Lead/Opportunity, 5 para Project, 'SaleOrder' para SaleOrder
  parentEntityId: number;
  doneNote?: string;
}

export interface ExtendedActivityParams {
  parentEntityType?: string | number;
  parentEntityId?: number;
  filterGroups?: any[][];
  startRow?: number;
  endRow?: number;
  sortDirection?: string;
  sortProperty?: string;
  filterProperty?: string;
  filterValue?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  private activityUrl = environment.coreApiBaseUrl + '/api/activity';
  private activityDoneUrl = environment.coreApiBaseUrl + '/api/activity/done';
  private activityCancelUrl =
    environment.coreApiBaseUrl + '/api/activity/cancel';
  private activityAdvancedUrl =
    environment.coreApiBaseUrl + '/api/activity-type/get/advanced';
  private activityExtendedUrl =
    environment.coreApiBaseUrl + '/api/activity/get/extended';
  private activityTypeUrl = environment.coreApiBaseUrl + '/api/activity-type';
  private partnerUrl = environment.coreApiBaseUrl + '/api/partner';

  constructor(
    private http: HttpClient,
    private companyService: CompanyService
  ) {}

  /**
   * Obtiene los tipos de actividad disponibles
   */
  getActivityTypes(getParams: GetParams): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const params = convertGetParamsToHttpParams(getParams);
    return this.http.get<GetResponse>(this.activityTypeUrl, {
      headers,
      params,
    });
  }
  createActivityTypes(activityType: ActivityType): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const url = environment.coreApiBaseUrl + '/api/activity-type';
    return this.http.post<PostResponse>(url, activityType, {
      headers,
    });
  }
  updateActivityTypes(activityType: ActivityType): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    const url = environment.coreApiBaseUrl + '/api/activity-type';
    return this.http.put(url, activityType, { headers });
  }
  deleteActivityTypes(ids: number[]): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.delete<PostResponse>(this.activityTypeUrl, {
      headers,
      body: { ids },
    });
  }

  /**
   * Obtiene actividades con filtros avanzados
   */
  getAdvancedActivities(getParams: GetAdvancedParams): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const payload = {
      ...getParams,
      companyId: [this.companyService.selectedCompanyId$.value || 0],
    };
    return this.http.post<GetResponse>(this.activityAdvancedUrl, payload, {
      headers,
    });
  }

  getActivityColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.activityUrl}/column-values`,
      payload,
      {headers}
    );
  }

  /**
   * Obtiene actividades extendidas con información completa (partner, company, parent entity)
   * POST /api/activity/get/extended
   */
  getExtendedActivities(getParams: ExtendedActivityParams, companyIds?: number[]): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const multiIds = companyIds || this.companyService.selectedCompanyIds$.getValue();
    const payload = {
      startRow: getParams.startRow || 0,
      endRow: getParams.endRow || 100,
      sortDirection: getParams.sortDirection || 'desc',
      sortProperty: getParams.sortProperty || 'id',
      companyId: multiIds.length > 0
        ? multiIds
        : [this.companyService.selectedCompanyId$.getValue() || 0],
    };
    return this.http.post<GetResponse>(this.activityExtendedUrl, payload, {
      headers,
    });
  }

  /**
   * Obtiene actividades por entidad padre (Lead, Opportunity o SaleOrder) usando query params
   * Ejemplo: /api/activity-type?parentEntityType=49&parentEntityId=49&startRow=0&endRow=100&sortDirection=desc&sortProperty=id
   * parentEntityType: 49 = Lead u Opportunity (ambos usan 49), SaleOrder usa su propio valor
   */
  getActivitiesByParentEntity(
    parentEntityType: 'Lead' | 'Opportunity' | 'SaleOrder',
    parentEntityId: number,
    startRow: number = 0,
    endRow: number = 100,
    sortDirection: string = 'desc',
    sortProperty: string = 'id'
  ): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();

    // Para SaleOrder enviar el string directamente, para Lead/Opportunity enviar el número
    let parentEntityTypeValue: string | number;
    if (parentEntityType === 'SaleOrder') {
      parentEntityTypeValue = 'SaleOrder';
    } else {
      // Lead y Opportunity ambos usan 49 (número)
      parentEntityTypeValue = PARENT_ENTITY_TYPE.LEAD; // 49
    }

    const params = {
      parentEntityType: parentEntityTypeValue.toString(),
      parentEntityId: parentEntityId.toString(),
      startRow: startRow.toString(),
      endRow: endRow.toString(),
      sortDirection,
      sortProperty,
    };
    return this.http.get<GetResponse>(this.activityUrl, { headers, params });
  }

  /**
   * Obtiene actividades por entidad padre usando el tipo numérico directamente
   * Útil para entidades como SaleOrder que no tienen un método específico
   */
  getActivitiesByParentEntityType(
    parentEntityType: number,
    parentEntityId: number,
    startRow: number = 0,
    endRow: number = 100,
    sortDirection: string = 'desc',
    sortProperty: string = 'id'
  ): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();

    const params = {
      parentEntityType: parentEntityType.toString(),
      parentEntityId: parentEntityId.toString(),
      startRow: startRow.toString(),
      endRow: endRow.toString(),
      sortDirection,
      sortProperty,
    };
    return this.http.get<GetResponse>(this.activityUrl, { headers, params });
  }

  /**
   * Obtiene una actividad por su ID
   */
  getActivityById(activityId: number): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<any>(`${this.activityUrl}/${activityId}`, { headers });
  }

  /**
   * Obtiene actividades de una oportunidad específica
   */
  getActivitiesByOpportunityId(opportunityId: number): Observable<GetResponse> {
    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: 100,
      sortProperty: 'id',
      sortDirection: 'desc',
      filterGroups: [
        [
          {
            property: 'opportunityId',
            operator: 'Equals',
            value: opportunityId,
          },
        ],
      ],
    };
    return this.getAdvancedActivities(params);
  }

  /**
   * Obtiene actividades de un lead específico
   */
  getActivitiesByLeadId(leadId: number): Observable<GetResponse> {
    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: 100,
      sortProperty: 'id',
      sortDirection: 'desc',
      filterGroups: [
        [
          {
            property: 'leadId',
            operator: 'Equals',
            value: leadId,
          },
        ],
      ],
    };
    return this.getAdvancedActivities(params);
  }

  /**
   * Crea una nueva actividad
   */
  createActivity(activity: CreateActivityPayload): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.activityUrl, activity, {
      headers,
    });
  }

  /**
   * Marca una actividad como completada (done)
   * PUT /api/activity-type/done
   */
  markAsDone(payload: MarkAsDonePayload): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(this.activityDoneUrl, payload, {
      headers,
    });
  }

  /**
   * Cancela una actividad
   * PUT /api/activity-type/cancel
   */
  cancelActivity(payload: MarkAsDonePayload): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(this.activityCancelUrl, payload, {
      headers,
    });
  }

  /**
   * Actualiza una actividad existente
   */
  updateActivity(activity: UpdateActivityPayload): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(this.activityUrl, activity, { headers });
  }

  /**
   * Actualiza parcialmente una actividad (PATCH)
   */
  patchActivity(patchPayload: {
    id: number;
    [key: string]: any;
  }): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(this.activityUrl, patchPayload, {
      headers,
    });
  }

  /**
   * Marca una actividad como completada
   */
  markAsCompleted(activityId: number): Observable<PostResponse> {
    return this.patchActivity({
      id: activityId,
      completed: true,
      completedDate: new Date().toISOString(),
    });
  }

  /**
   * Elimina una o varias actividades
   */
  deleteActivities(activityIds: number[], parentEntityType?: number, parentEntityId?: number): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    const body: any = { ids: activityIds };
    
    // Si se proporciona información de la entidad padre, incluirla
    if (parentEntityType !== undefined && parentEntityId !== undefined) {
      body.parentEntityType = parentEntityType;
      body.parentEntityId = parentEntityId;
    }
    
    return this.http.delete(this.activityUrl, {
      headers,
      body,
    });
  }

  /**
   * Elimina una actividad por ID
   */
  deleteActivity(activityId: number, parentEntityType?: number, parentEntityId?: number): Observable<any> {
    return this.deleteActivities([activityId], parentEntityType, parentEntityId);
  }

  /**
   * Obtiene partners (comerciales) para asignar a actividades
   * GET /api/partner con query params de paginación
   */
  getPartners(
    startRow: number = 0,
    endRow: number = 100,
    sortProperty: string = 'id',
    sortDirection: string = 'asc'
  ): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const params = {
      startRow: startRow.toString(),
      endRow: endRow.toString(),
      sortProperty,
      sortDirection,
    };
    return this.http.get<GetResponse>(this.partnerUrl, { headers, params });
  }
}
